import { yupResolver } from "@hookform/resolvers/yup";
import {
  AddressCard,
  AddressSearchField,
  Button,
  FieldError,
  Flex,
  FormGroupRow,
  FormLayout,
  FormLegend,
  addressHelpers,
  statesByCountry,
  useAddressAutocompleteFlow,
  type AddressDraft,
  type CountryCode,
  type Item,
} from "@wayflyer/flyui";
import { RhfDropdownField, RhfTextField } from "@wayflyer/flyui-hook-form";
import { useCallback, useState, type KeyboardEvent } from "react";
import { useForm, type FieldValues, type Path, type PathValue, type UseFormReturn } from "react-hook-form";
import { type IntlShape, useIntl } from "react-intl";
import * as yup from "yup";

import { useJurisdictionLabels } from "../rhf-address-search-field/jurisdiction-labels";

import { MESSAGES } from "./message-definitions";
import styles from "./rhf-progressive-address-field.module.css";
import { usePlacesAddressSuggestions } from "./use-places-address-suggestions";

export type ProgressiveAddressFieldMapping<T extends FieldValues> = Record<keyof AddressDraft, Path<T>>;

export interface RhfProgressiveAddressFieldProps<T extends FieldValues, TContext = unknown, TTransformedValues = T> {
  form: UseFormReturn<T, TContext, TTransformedValues>;
  /** Parent form fields that receive the address once it is confirmed, and are cleared while it isn't. */
  fieldMapping: ProgressiveAddressFieldMapping<T>;
  label: string;
  helperText?: string;
  /** Country the address must be in. Searches are restricted to it and it cannot be changed. */
  countryCode: CountryCode;
  disabled?: boolean;
  /** Validation error from the parent form, shown in whichever step the field is in. */
  errorMessage?: string;
}

const US_ZIP_CODE_REGEX = /(^\d{5}$)|(^\d{5}-\d{4}$)/;

const ADDRESS_KEYS: (keyof AddressDraft)[] = [
  "addressLine1",
  "addressLine2",
  "city",
  "state",
  "postalCode",
  "countryCode",
];

const statesFor = (countryCode: string): Item[] => (statesByCountry as Record<string, Item[]>)[countryCode] ?? [];

const makeDraftSchema = ({
  intl,
  countryCode,
  labels,
}: {
  intl: IntlShape;
  countryCode: CountryCode;
  labels: { addressLine1: string; city: string; state: string; postalCode: string };
}): yup.ObjectSchema<AddressDraft> => {
  const required = (fieldLabel: string) => intl.formatMessage(MESSAGES.fieldRequired, { fieldLabel });
  const postalCode = yup.string().trim().required(required(labels.postalCode));

  return yup.object({
    addressLine1: yup.string().trim().required(required(labels.addressLine1)),
    addressLine2: yup.string().defined(),
    city: yup.string().trim().required(required(labels.city)),
    state: yup.string().required(required(labels.state)),
    postalCode:
      countryCode === "US"
        ? postalCode.matches(US_ZIP_CODE_REGEX, intl.formatMessage(MESSAGES.invalidUsZipCode))
        : postalCode,
    countryCode: yup.string().required(),
  });
};

/**
 * Progressive-disclosure address entry: search, then review the address in editable
 * fields, then confirm it into a card. Built on flyui's `useAddressAutocompleteFlow`.
 *
 * The in-progress address lives in this component's own form. The parent form only
 * holds a confirmed address, so its validation never sees a half-entered one.
 */
export const RhfProgressiveAddressField = <T extends FieldValues, TContext = unknown, TTransformedValues = T>({
  form,
  fieldMapping,
  label,
  helperText,
  countryCode,
  disabled,
  errorMessage,
}: RhfProgressiveAddressFieldProps<T, TContext, TTransformedValues>) => {
  const intl = useIntl();
  const { getStateLabel, getPostalCodeLabel } = useJurisdictionLabels();
  const { searchAddressSuggestions, resolveAddressSuggestion } = usePlacesAddressSuggestions();

  const labels = {
    addressLine1: intl.formatMessage(MESSAGES.addressLine1Label),
    city: intl.formatMessage(MESSAGES.cityLabel),
    state: getStateLabel(countryCode),
    postalCode: getPostalCodeLabel(countryCode),
  };
  const draftSchema = makeDraftSchema({ intl, countryCode, labels });

  const [initialAddress] = useState<AddressDraft | null>(() => {
    const address = Object.fromEntries(
      ADDRESS_KEYS.map((key) => [key, String(form.getValues(fieldMapping[key]) ?? "")])
    ) as AddressDraft;
    return address.addressLine1 ? { ...address, countryCode } : null;
  });

  const draftForm = useForm<AddressDraft>({
    defaultValues: initialAddress ?? addressHelpers.createEmptyDraft(countryCode),
    resolver: yupResolver(draftSchema),
    reValidateMode: "onChange",
  });

  const resetDraft = useCallback(
    (address: AddressDraft) => draftForm.reset({ ...address, countryCode }),
    [draftForm, countryCode]
  );

  const flow = useAddressAutocompleteFlow({
    countryCode,
    initialAddress,
    onDraftAddressChange: resetDraft,
    resolveAddressSuggestion,
    searchAddressSuggestions,
  });

  const writeToParentForm = (address: AddressDraft | null) => {
    const values = address ?? addressHelpers.createEmptyDraft(countryCode);
    for (const key of ADDRESS_KEYS) {
      const value = key === "countryCode" ? countryCode : values[key];
      form.setValue(fieldMapping[key], value as PathValue<T, Path<T>>, { shouldDirty: true });
    }
    // One pass rather than per field: parent schemas can include slow async tests.
    if (address) {
      void form.trigger(ADDRESS_KEYS.map((key) => fieldMapping[key]));
    }
  };

  const confirmDraft = () =>
    draftForm.handleSubmit((draft) => {
      const address = { ...draft, countryCode };
      flow.confirmAddress(address);
      writeToParentForm(address);
    })();

  const editAddress = () => {
    flow.editAddress();
    writeToParentForm(null);
  };

  const cancelDraft = () => {
    flow.cancelAddressForm();
    if (flow.confirmedAddress) {
      writeToParentForm(flow.confirmedAddress);
    }
  };

  const removeAddress = () => {
    flow.removeAddress();
    writeToParentForm(null);
  };

  // The fields sit inside the parent's <form>, so Enter would otherwise submit the whole page.
  const handleDraftKeyDown = (event: KeyboardEvent<HTMLFieldSetElement>) => {
    if (event.key === "Enter" && event.target instanceof HTMLInputElement) {
      event.preventDefault();
      void confirmDraft();
    }
  };

  const cardLines = flow.confirmedAddress ? addressHelpers.getAddressCardLinesFromDraft(flow.confirmedAddress) : null;
  const stateOptions = statesFor(countryCode);
  const addressLine1Field = (
    <RhfTextField<AddressDraft> form={draftForm} name="addressLine1" label={labels.addressLine1} disabled={disabled} />
  );
  const errorName = `${fieldMapping.addressLine1}-address`;

  if (flow.mode === "search") {
    return (
      <div data-analytics-name={fieldMapping.addressLine1}>
        <AddressSearchField
          key={flow.searchFieldKey}
          {...flow.addressSearchFieldProps}
          onManualEntry={disabled ? undefined : flow.addressSearchFieldProps.onManualEntry}
          name={`${fieldMapping.addressLine1}-search`}
          label={label}
          helperText={helperText}
          placeholder=""
          manualEntryLabel={intl.formatMessage(MESSAGES.enterManually)}
          error={flow.error ?? errorMessage}
          disabled={disabled}
        />
      </div>
    );
  }

  // A definite width stops AddressCard's container query inflating this field's height inside FormLayout.
  return (
    <Flex direction="column" width="100%" dataAttributes={{ "data-analytics-name": fieldMapping.addressLine1 }}>
      <fieldset
        className={styles.fieldset}
        disabled={disabled}
        onKeyDown={flow.mode === "form" ? handleDraftKeyDown : undefined}
      >
        <FormLegend helperText={helperText}>{label}</FormLegend>
        <Flex direction="column" gap="3" margin={["3", "0", "0", "0"]}>
          {flow.mode === "form" ? (
            <Flex direction="column" gap="4">
              <FormLayout>
                {flow.showAddressLine2 ? (
                  <>
                    {addressLine1Field}
                    <RhfTextField<AddressDraft>
                      form={draftForm}
                      name="addressLine2"
                      label={intl.formatMessage(MESSAGES.addressLine2Label)}
                      optional
                      disabled={disabled}
                    />
                  </>
                ) : (
                  <Flex direction="column" gap="var(--space-component-md)">
                    {addressLine1Field}
                    <Flex justify="flex-start">
                      <Button
                        disabled={disabled}
                        onClick={flow.showAddressLine2Field}
                        size="small"
                        type="button"
                        variant="Ghost"
                      >
                        {intl.formatMessage(MESSAGES.addAddressLine2)}
                      </Button>
                    </Flex>
                  </Flex>
                )}
                <RhfTextField<AddressDraft> form={draftForm} name="city" label={labels.city} disabled={disabled} />
                <FormGroupRow>
                  {stateOptions.length > 0 ? (
                    <RhfDropdownField<AddressDraft>
                      form={draftForm}
                      name="state"
                      label={labels.state}
                      options={stateOptions}
                      disabled={disabled}
                    />
                  ) : (
                    <RhfTextField<AddressDraft>
                      form={draftForm}
                      name="state"
                      label={labels.state}
                      disabled={disabled}
                    />
                  )}
                  <RhfTextField<AddressDraft>
                    form={draftForm}
                    name="postalCode"
                    label={labels.postalCode}
                    disabled={disabled}
                  />
                </FormGroupRow>
              </FormLayout>
              <Flex align="center" gap="4" wrap="wrap">
                <Button disabled={disabled} onClick={confirmDraft} size="small" type="button" variant="Secondary">
                  {intl.formatMessage(MESSAGES.confirmAddress)}
                </Button>
                <Button disabled={disabled} onClick={cancelDraft} size="small" type="button" variant="Tertiary">
                  {intl.formatMessage(MESSAGES.cancel)}
                </Button>
              </Flex>
              {errorMessage ? (
                <FieldError name={errorName} error={intl.formatMessage(MESSAGES.confirmToContinue)} />
              ) : null}
            </Flex>
          ) : null}

          {flow.mode === "confirmed" && cardLines ? (
            <>
              <AddressCard
                addressLineStrong={cardLines.addressLineStrong}
                addressLineSubtle={cardLines.addressLineSubtle}
                onEdit={{
                  content: intl.formatMessage(MESSAGES.edit),
                  accessibilityLabel: intl.formatMessage(MESSAGES.editAddress),
                  disabled,
                  onAction: editAddress,
                }}
                onRemove={
                  disabled
                    ? undefined
                    : {
                        content: intl.formatMessage(MESSAGES.remove),
                        accessibilityLabel: intl.formatMessage(MESSAGES.removeAddress),
                        onAction: removeAddress,
                      }
                }
              />
              {errorMessage ? <FieldError name={errorName} error={errorMessage} /> : null}
            </>
          ) : null}
        </Flex>
      </fieldset>
    </Flex>
  );
};
